import * as H from "history";
import { flatMap, isEqual } from "lodash";
import * as React from "react";
import { Link, match as Match } from "react-router-dom";
import { NetworkAssessmentAnalysisType, ProjectRunConfigurationType } from "@app/projects/enumerators";
import { ProjectRouteParams } from "@app/projects/projectRouteParams";
import { CountyProjectContextProps, withCountyProjectContext } from "@app/projects/CountyProjectContext";
import Pie from "@app/shared/pie";
import { ProgressBarBand } from "@app/shared/progressBarBand";
import { PrimaryTable as SkeletonTable } from "@app/shared/skeleton-components";
import { ColumnConfig, SortDirections, TextAlignment } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { AccuracyTemplate, AccuracyTemplateInspectionGroup, AccuracyTemplateScoringBand, AccuracyTemplateScoringBandGroup } from "@app/templates/accuracy/models";
import { bandDisplayNames } from "@app/projects/servicing-provider/accuracy/provider-inspection-profile-components/utils";
import { JSX } from "react";
import TooltipWrapper from "@app/shared/TooltipWrapper";
import { ProjectRunConfigurationResult } from "@app/projects/projectRunConfigurationResult";
import RegionFilters from "@app/projects/regionFilters";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { AccuracyAggregateInspectionResultGroup, AccuracyAggregateInspectionResult, AccuracyInspectionResponse, AccuracyInspectionResultReasonsPerGroup } from "@app/projects/results/accuracy/accuracyAggregateInspectionResultGroup";
import { AccuracyBandAggregate } from "@app/projects/results/accuracy/accuracyBandAggregate";
import { AccuracyTemplateContext } from "@app/templates/accuracy/components";
import { InspectorGroupNames } from "@app/projects/network-assessment/accuracy/accuracyConstants";
import { getInspectorTooltip } from "@app/projects/network-assessment/accuracy/AccuracyInspectionWaffleTooltips";
import AccuracyService from "@app/projects/network-assessment/accuracy/accuracyService";
import AccuracyTooltipLegend from "@app/projects/network-assessment/accuracy/AccuracyTooltipLegend";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

const accuracyService = new AccuracyService();

type FlatAggregateRecord = Pick<AccuracyAggregateInspectionResultGroup, "groupName" | "inspectionGroupUid"> & AccuracyAggregateInspectionResult;

type AccuracyAggregateInspectionDetailsByGroupProps = CountyProjectContextProps & {
  networkAssessmentAnalysisType: NetworkAssessmentAnalysisType;
  compareToRunConfiguration?: ProjectRunConfigurationResult;
  exportEndpoint: string;
  location: H.Location<ProjectRouteParams>;
  match: Match;
  projectId: number;
  regionFilters: RegionFilters;
  runConfiguration: ProjectRunConfigurationResult;
  setDualMembershipMode: (isDualMembershipMode: boolean, projectRunConfigurationType?: ProjectRunConfigurationType, primaryConfigurationId?: number, compareToRunConfigurationId?: number, forceLoading?: boolean) => void;
}

interface AccuracyAggregateInspectionDetailsByGroupoState {
  activeView: string;
  aggregatedRecords: FlatAggregateRecord[];
  dataByInspectionGroup: Map<string, AccuracyAggregateInspectionByGroupWafflesToppings[]>;
  dataExpansions: Map<string, AccuracyAggregateInspectionByGroupWafflesToppings[]>;
  error: boolean;
  isSorting: boolean;
  records: FlatAggregateRecord[];
  selectedSortingColor: string;
  sortedColumn: string;
  sortedRecords: FlatAggregateRecord[];
  sortingColors: string[];
}

class AccuracyAggregateInspectionByGroupWafflesToppings {
  public inspector: React.ReactNode;
  public total: React.ReactNode;
  public key: string;
  public totalCount: React.ReactNode;

  constructor(inspector: React.ReactNode, total: React.ReactNode, totalCount: React.ReactNode, bandCounts: BandCountNode[], key: string) {
    this.inspector = inspector;
    this.total = total;
    this.key = key;
    this.totalCount = totalCount;

    for (const bandCount of bandCounts) {
      Object.assign(this, {
        [`${bandCount.band}-Total`]: bandCount.providerCount
      });
    }
  }
}

class BandCountNode {
  public band: string;
  public providerCount: React.ReactNode;

  constructor(band: string, providerCount: React.ReactNode) {
    this.band = band;
    this.providerCount = providerCount;
  }
}

class AccuracyAggregateInspectionDetailsByGroup extends React.Component<AccuracyAggregateInspectionDetailsByGroupProps, AccuracyAggregateInspectionDetailsByGroupoState> {
  private bandGroups: AccuracyTemplateScoringBandGroup[] = [];
  private inspectionGroups: AccuracyTemplateInspectionGroup[] = [];

  constructor(props: AccuracyAggregateInspectionDetailsByGroupProps) {
    super(props);

    this.state = {
      activeView: null,
      aggregatedRecords: [],
      dataByInspectionGroup: new Map<string, AccuracyAggregateInspectionByGroupWafflesToppings[]>(),
      dataExpansions: new Map<string, AccuracyAggregateInspectionByGroupWafflesToppings[]>(),
      error: false,
      isSorting: false,
      records: [],
      selectedSortingColor: null,
      sortedColumn: null,
      sortedRecords: [],
      sortingColors: [],
    };
  }

  public async componentDidMount() {
    this.useAggregateResults(this.props.projectId, this.props.networkAssessmentAnalysisType, this.props.regionFilters, this.props.runConfiguration);

    if (this.props.location.state && this.props.location.state.runConfiguration) {
      const config = this.props.location.state.runConfiguration;
      const compareToConfig = this.props.location.state.compareToRunConfiguration;
      await this.props.setDualMembershipMode(this.props.location.state.isDualMembershipMode, config.projectRunConfigurationType, config.projectRunConfigurationId, compareToConfig ? compareToConfig.projectRunConfigurationId : undefined);
    }
  }

  public async componentDidUpdate(prevProps: AccuracyAggregateInspectionDetailsByGroupProps, prevState: AccuracyAggregateInspectionDetailsByGroupoState) {
    if (!isEqual(prevState.records, this.state.records)) {
      this.createDataByInspectionGroup();
    }

    if (!this.state.activeView && this.inspectionGroups.length > 0)
      this.setState({
        activeView: `${this.inspectionGroups[0].name}-${this.inspectionGroups[0].uid}`
    });

    if (prevProps.projectId !== this.props.projectId || prevProps.networkAssessmentAnalysisType !== this.props.networkAssessmentAnalysisType || prevProps.regionFilters !== this.props.regionFilters || prevProps.runConfiguration !== this.props.runConfiguration) {
      this.useAggregateResults(this.props.projectId, this.props.networkAssessmentAnalysisType, this.props.regionFilters, this.props.runConfiguration);

      this.setState({
        isSorting: false,
        sortedColumn: null,
      });
    }
  }

  public render() {
    const haveResultsToRender = this.state.sortedRecords.length > 0;

    return (
      <AccuracyTemplateContext.Consumer>
        {(accuracyTemplate) => (
          <>
            {!haveResultsToRender &&
              <>
                {this.state.error &&
                  <QESAlert type="danger" persist={true}>
                    <strong>Something went wrong!</strong>
                    Results &quot;by inspection&quot; could not be retrieved.
                  </QESAlert>
                }
                <SkeletonTable />
              </>
            }
            {(haveResultsToRender && accuracyTemplate) && this.buildWaffles(accuracyTemplate)}
          </>
        )}
      </AccuracyTemplateContext.Consumer>
    );
  }

  private getGroupUid = (waffleToppingKey: string) => {
    const record = this.state.records.find((x) => x.inspectionUid === waffleToppingKey);

    if (record) {
      return record.inspectionGroupUid;
    }
  };

  private createDataByInspectionGroup() {
    const dataByInspectionGroup = new Map<string, AccuracyAggregateInspectionByGroupWafflesToppings[]>();

    const waffleToppings = this.getWaffleToppings([...this.state.sortedRecords]);

    for (const r of this.state.aggregatedRecords) {
      const value = waffleToppings.filter((x) => this.getGroupUid(x.key.replace(`${r.groupName}-`, "")) === r.inspectionGroupUid);
      dataByInspectionGroup.set(this.getPrefixedKey(r), [...value]);
    }

    this.setState({
      dataByInspectionGroup,
    });
  }

  private buildWaffles = (accuracyTemplate: AccuracyTemplate) => {
    this.storeInspectionsAndBands(accuracyTemplate);

    let data: AccuracyAggregateInspectionByGroupWafflesToppings[];
    if (this.state.isSorting) {
      data = this.getWaffleToppings(this.state.sortedRecords);
    } else {
      data = this.state.dataByInspectionGroup.get(this.state.activeView) ?? [];
    }

    return (
      <>
        <Waffles<AccuracyAggregateInspectionByGroupWafflesToppings>
          bulkActionBar={this.getBulkActionBar(accuracyTemplate)}
          columns={this.getColumnConfig(accuracyTemplate)}
          customSort={this.customSort}
          data={data}
          dataExpansions={this.state.dataExpansions}
          fullWidth={true}
          loading={!this.state.records.length}
          passThroughSorting={true}
          testId={`accuracy-aggregate-inspection-${this.state.activeView}`}
          totalNumberOfRecords={data.length}
        />
      </>
    );
  }

  /**
   * Retrieves aggregate results for a given run configuration.
   * If no run configuration is supplied, returns an empty array.
   *
   * Transforms the retrieved records into a flat structure.
   * @param runConfiguration The run configuration.
   * @param networkAssessmentAnalysisType The analysis type.
   */
  private useAggregateResults = (projectId: number, networkAssessmentAnalysisType: NetworkAssessmentAnalysisType, regionFilters: RegionFilters, runConfiguration?: ProjectRunConfigurationResult) => {
    if (!runConfiguration) {
      this.setState({
        error: true,
      });
    } else {
      const {
        county: countyRegionCode,
        specialty: specialtyGroupCode,
        state: stateRegionCode,
      } = regionFilters || {} as RegionFilters;

      let regionSetId: number;
      if (!stateRegionCode && !countyRegionCode) {
        regionSetId = runConfiguration.serviceAreaRegionSetId;
      } else if (stateRegionCode && !countyRegionCode) {
        regionSetId = runConfiguration.getState(stateRegionCode).canonicalRegionSetId;
      } else if (countyRegionCode) {
        regionSetId = runConfiguration.getCounty(countyRegionCode).canonicalRegionSetId;
      }

      accuracyService
        .accuracyAggregateInspectionResults(projectId, runConfiguration.projectRunConfigurationId, networkAssessmentAnalysisType, regionSetId, specialtyGroupCode)
        .then((res) => this.processAccuracyInspectionResponse(res), () => this.setState({ error: true }));
    }
  }

  private processAccuracyInspectionResponse = (response: AccuracyInspectionResponse) => {
    if (!response) return;

    this.transformAggregates(response.aggregates);
    this.transformInspectorResultReasons(response.inspectionResultReasons);
  }

  private transformInspectorResultReasons = (breakdowns: AccuracyInspectionResultReasonsPerGroup[]) => {
    const dataExpansions = new Map<string, AccuracyAggregateInspectionByGroupWafflesToppings[]>();

    for (const results of breakdowns) {

      const tmpArray: AccuracyAggregateInspectionByGroupWafflesToppings[] = [];
      const temp = new AccuracyAggregateInspectionByGroupWafflesToppings(null, null, null, [], `Expansion_${results.inspectionUid}`);
      Object.assign(temp, { "Accurate-Total": this.generateExpansionLines(results.accurate) });
      Object.assign(temp, { "Suspect-Total": this.generateExpansionLines(results.suspect) });
      Object.assign(temp, { "Inaccurate-Total": this.generateExpansionLines(results.inaccurate) });
      Object.assign(temp, { "Enriched-Total": this.generateExpansionLines(results.enriched) });
      Object.assign(temp, { "Skipped-Total": this.generateExpansionLines(results.skipped) });

      tmpArray.push(temp);

      dataExpansions.set(results.inspectionUid, tmpArray);
    }

    this.setState({
      dataExpansions
    });
  }

  private generateExpansionLines = (reasons: Map<string, number>) : React.ReactNode => {
    const lines : React.ReactNode[] = [];

    if (reasons) {
      reasons = new Map(Object.entries(reasons));
      for (const [key, value] of reasons)
        lines.push(<div key={key}>{key}: {value.toLocaleString()}</div>);
    }

    return (
      <div className="d-inline-block text-end m-auto">
        {lines}
      </div>
    );
  }


  /**
   * Transforms the aggregates into a flattened array of records for use in the table.
   * @param response The response to transform.
   */
  private transformAggregates = (aggregates: AccuracyAggregateInspectionResultGroup[]) => {
    const aggregatedRecords: FlatAggregateRecord[] = [];

    for (const group of aggregates) {
      const flatAggregateRecord: FlatAggregateRecord = {
        accuracyIndex: null,
        bandAggregates: group.groupBandAggregates,
        groupName: group.groupName,
        inspectionGroupUid: group.inspectionGroupUid,
        inspectionUid: group.inspectionGroupUid,
        name: group.groupName,
        providerCount: null,
        providerFailPercentage: null,
        providerPassPercentage: null,
      };

      aggregatedRecords.push(flatAggregateRecord);
    }

    const records = flatMap(aggregates, (aggregate) => {
      return aggregate.results.map<FlatAggregateRecord>((result) => ({
        groupName: aggregate.groupName,
        inspectionGroupUid: aggregate.inspectionGroupUid,
        ...result,
      }));
    });

    // stores the possible color options for sorting the accuracy pie chart
    const sortingColors: string[] = [];

    for (const record of records) {
      const bands: ProgressBarBand[] = this.getProgressBarBands(record.bandAggregates);

      for (const band of bands) {
        if (!sortingColors.includes(band.color)) {
          sortingColors.push(band.color);
        }
      }
    }

    // in order to avoid modifying the original records' sort order when a sort occurs, clone them
    this.setState({
      aggregatedRecords: aggregatedRecords,
      records: records,
      sortedRecords: [...records],
      sortingColors,
    });
  }

  private storeInspectionsAndBands = (accuracyTemplate: AccuracyTemplate) => {
    if (!isEqual(this.inspectionGroups, accuracyTemplate.criteria.inspectionGroups)) {
      this.inspectionGroups = accuracyTemplate.criteria.inspectionGroups;
    }

    if (!isEqual(this.bandGroups, accuracyTemplate.scoring.bandGroups)) {
      this.bandGroups = accuracyTemplate.scoring.bandGroups;
    }
  }

  private getBulkActionBar = (accuracyTemplate: AccuracyTemplate) => {
    return (
      <>
        <div className='tabs-style-2'>
          <ul className='nav nav-tabs'>
            {accuracyTemplate.criteria.inspectionGroups.map((inspectionGroup) => (
              <li className='nav-item' key={inspectionGroup.name}>
                <a className={`nav-link${this.state.activeView === `${inspectionGroup.name}-${inspectionGroup.uid}` ? " active" : ""}`}
                  role="tab"
                  onClick={() => this.setState(
                      {
                        activeView: `${inspectionGroup.name}-${inspectionGroup.uid}`,
                        isSorting: false,
                      }
                    )}>
                    {inspectionGroup.name}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </>
    );
  }

  private getColumnConfig = (accuracyTemplate: AccuracyTemplate) => {
    const columns: ColumnConfig = {
      inspector: {
        displayText: "Inspector",
        noResize: true,
        sizeContentToFit: true,
        sticky: true,
        testId: "inspector",
      },
      total: {
        alignment: TextAlignment.Center,
        displayText: "Total",
        handleSpecialSort: this.setSelectedSortingColor,
        noResize: true,
        sizeContentToFit: true,
        specialSort: this.createSpecialSortElement(),
        testId: "total",
      },
      totalCount: {
        alignment: TextAlignment.Center,
        displayText: "Total Count",
        noResize: true,
        noSort: true,
        sizeContentToFit: true,
        testId: "total-count",
      },
    };

    const bandGroups = this.filterBandGroupsByInspectorGroup(this.state.activeView?.split('-')[0]);
    accuracyTemplate.scoring.bandGroups.forEach((bg) => {
      bg.bands.forEach((b) => {
        columns[`${b.name}-Total`] = {
          alignment: TextAlignment.Center,
          displayText: `Total ${bandDisplayNames(b.name)}`,
          hidden: !bandGroups.some(g => g.name === b.name),
          noResize: true,
          sizeContentToFit: true,
          testId: b.uid + "-total",
        };
      })
    })

    return columns;
  }

  private getWaffleToppings = (records: FlatAggregateRecord[]) => {
    if (records.length === 0) {
      return [];
    }

    const waffleToppings: AccuracyAggregateInspectionByGroupWafflesToppings[] = records.map((sr) => {
      const tooltip = getInspectorTooltip(sr.inspectionUid) && (
        <TooltipWrapper text={getInspectorTooltip(sr.inspectionUid)}>
          <QesIcon iconName={QesIcons.InfoCircle} description="Info" className="align-top" />
        </TooltipWrapper>
      );
      const inspector: React.ReactNode = <div> {sr.name} {tooltip} </div>;

      const bands: ProgressBarBand[] = this.getProgressBarBands(sr.bandAggregates);

      const total: React.ReactNode | "N/A" = bands.length > 0 ? (
        <div className="large-pie-chart-container">
          <TooltipWrapper
            text={
              <AccuracyTooltipLegend
                inspectionName={sr.name}
                progressBarBands={bands}
              />
            }
            position="bottom"
          >
            <Pie segments={bands.map((band) => ({ fill: band.color, percent: band.percent / 100 }))} />
          </TooltipWrapper>
        </div>
      ) : "N/A";

      const totalCount: React.ReactNode = <div>
          {sr.bandAggregates.reduce((count, {providerCount}) => count + providerCount, 0).toLocaleString()}
      </div>;

      const inspectorBands = this.filterBandGroupsByInspectorGroup(sr.groupName);
      const bandCounts: BandCountNode[] = inspectorBands.map<BandCountNode>((band) => {
        const bandAggregate = sr.bandAggregates.find((bandAggregate) => (bandAggregate.name === band.name))
        if(bandAggregate)
          return     {
            band: bandAggregate.name,
            providerCount: <Link
              key={`${band.name}_${sr.inspectionUid}`}
              to={{ pathname: `${this.props.match.url}${this.linkSuffix()}/inspection/${encodeURIComponent(sr.inspectionUid)}/band/${encodeURIComponent(bandAggregate.name)}`, search: location.search }}
              >
              {`${bandAggregate.providerCount.toLocaleString()} (${bandAggregate.bandPercentage.toFixed(1)}%)`}
            </Link>
           };
        return {
          band: band.name,
          providerCount: <div>---</div>
        };
      });

      const key = sr.inspectionUid;

      return new AccuracyAggregateInspectionByGroupWafflesToppings(inspector, total, totalCount, bandCounts, key);
    });

    return waffleToppings;
  }

  private filterBandGroupsByInspectorGroup = (inspectorGroup: string) => {
      const bandGroups = flatMap(this.bandGroups, (bandGroup) => bandGroup.bands);

      switch(inspectorGroup){
        case InspectorGroupNames.Enrichment:
          return bandGroups.filter((band) => (band.name == "Enriched" || band.name == "Skipped"));
        case InspectorGroupNames.Completeness:
          return bandGroups.filter((band) => (band.name == "Accurate" || band.name == "Inaccurate"));
        case InspectorGroupNames.Eligibility:
          return bandGroups.filter((band) => (band.name == "Accurate" || band.name == "Inaccurate" || band.name == "Skipped"));
        case InspectorGroupNames.Standardization:
          return bandGroups.filter((band) => (band.name == "Accurate" || band.name == "Inaccurate" || band.name == "Skipped" || band.name == "Suspect"))
        case InspectorGroupNames.Validity:
        case InspectorGroupNames.Integrity:
          return bandGroups.filter((band) => (band.name == "Accurate" || band.name == "Inaccurate" || band.name == "Skipped" || band.name == "Suspect" || band.name == "Enriched"));
        default:
          return bandGroups;
      }
  };

  private linkSuffix = () => {
    let suffix = "";
    if (this.props.regionFilters.state) {
      suffix += `/state/${this.props.regionFilters.state}`;
    }
    if (this.props.regionFilters.county) {
      suffix += `/county/${this.props.regionFilters.county}`;
    }
    if (this.props.regionFilters.specialty) {
      suffix += `/specialty/${this.props.regionFilters.specialty}`;
    }
    return suffix;
  }

  private createSpecialSortElement = () => {
    const band: AccuracyTemplateScoringBand = flatMap(this.bandGroups, (bandGroup) => bandGroup.bands).find((x) => x.bandColor === this.state.selectedSortingColor);

    let tooltipText: JSX.Element;
    if (band) {
      if (this.state.sortingColors.length > 1) {
        tooltipText = (
          <>
            Sorting By: {band.name}
          </>
        );
      } else {
        tooltipText = (
          <>
            All rows are {band.name}
          </>
        );
      }
    }

    return (
      <TooltipWrapper
        text={tooltipText}
        position="bottom"
      >
        <QesIcon
          iconName={QesIcons.Circle}
          pathStyle={{ fill: this.state.selectedSortingColor, stroke: "none" }}
          isDecorative
          className="cursor-pointer align-text-top"
        />
      </TooltipWrapper>
    );
  }

  private setSelectedSortingColor = (column: keyof AccuracyAggregateInspectionByGroupWafflesToppings) => {
    // determines the next selected sorting color on circle click
    if (this.state.sortingColors.length > 0) {
      if (this.state.selectedSortingColor && this.state.sortedColumn === column) {
        const index = this.state.sortingColors.indexOf(this.state.selectedSortingColor);

        if (index === this.state.sortingColors.length - 1) {
          // if the currently selected sorting color is the last in the array of possible color options,
          // set the first option in said array as the next selected sorting color
          this.setState({
            selectedSortingColor: this.state.sortingColors[0],
          });
        } else {
          // otherwise, select the next option
          this.setState({
            selectedSortingColor: this.state.sortingColors[index + 1],
          });
        }
      } else {
        // if no color is selected or sorting is occurring in a new column,
        // set the first option in the array of possible color options as the next selected sorting color
        this.setState({
          selectedSortingColor: this.state.sortingColors[0],
        });
      }
    }
  }

  private getSortingColor = (column: keyof AccuracyAggregateInspectionByGroupWafflesToppings) => {
    let sortingColor: string = null;
    if (this.state.selectedSortingColor && this.state.sortedColumn === column) {
      // if a selected sorting color exists and the last sorted column is this column,
      // use the selected sorting color stored in the state
      sortingColor = this.state.selectedSortingColor;
    } else {
      // if a sorting color hasn't been selected, or the last column that was sorted is not this column,
      // then select the first option from the color map
      if (this.state.sortingColors.length > 0) {
        sortingColor = this.state.sortingColors[0];

        this.setState({
          selectedSortingColor: sortingColor,
        });
      }
    }

    return sortingColor;
  }

  private customSort = (records: AccuracyAggregateInspectionByGroupWafflesToppings[], column: keyof AccuracyAggregateInspectionByGroupWafflesToppings, direction: string) => {
    let sortedRecords: FlatAggregateRecord[] = [];
    let isSorting = false;

    if (direction === SortDirections.Asc || direction === SortDirections.Desc) {
      isSorting = true;

      // re-creates a list of distinct provider count and percentage columns based on scoring band uids
      const providerCountBands: string[] = [];
      for (const bandGroup of this.bandGroups) {
        for (const band of bandGroup.bands) {
          const providerCountBand: string = band.name + "-Total";
          providerCountBands.push(providerCountBand);
        }
      }

      // retrieves the sorted column's provider count for sorting purposes
      const bandProviderCountSortValue = (bandAggregates: AccuracyBandAggregate[]) => {
        const bandAggregate: AccuracyBandAggregate = bandAggregates.find((x) => x.name + "-Total" === column);

        if (bandAggregate) {
          return bandAggregate.providerCount;
        }

        return 0;
      };

      // retrieves the sorted column's sorting color band percentage
      const accuracyBandPercentage = (bandAggregates: AccuracyBandAggregate[]) => {
        const total: number = bandAggregates.reduce((sum, x) => sum + x.providerCount, 0);
        const sortingColor: string = this.getSortingColor(column);
        const bandAggregate: AccuracyBandAggregate = bandAggregates.find((x) => x.color === sortingColor);

        if (bandAggregate) {
          return total > 0 ? (bandAggregate.providerCount / total) * 100 : 0;
        }

        return 0;
      };

      const multiplier = direction === SortDirections.Asc ? 1 : -1;
      const activeViewRecords = this.state.records.filter((r) => (this.state.activeView.includes(r.groupName)));

      if (providerCountBands.includes(column)) {
        sortedRecords = activeViewRecords.sort((a, b) => (bandProviderCountSortValue(a.bandAggregates) > bandProviderCountSortValue(b.bandAggregates) ? 1 : -1) * multiplier);
      }
      else {
        switch (column) {
          case "inspector":
            sortedRecords = activeViewRecords.sort((a, b) => (a.name + a.groupName > b.name + b.groupName ? 1 : -1) * multiplier);
            break;
          case "total":
            sortedRecords = activeViewRecords.sort((a, b) => (accuracyBandPercentage(a.bandAggregates) > accuracyBandPercentage(b.bandAggregates) ? 1 : -1) * multiplier);
            break;
          default:
            break;
        }
      }
    } else {
      // in order to avoid modifying the original records' sort order next time a sort occurs, clone them
      sortedRecords = JSON.parse(JSON.stringify(this.state.records));
    }

    this.setState({
      isSorting,
      sortedColumn: column,
      sortedRecords,
    });

    return records;
  }

  private getProgressBarBands = (bandAggregates: AccuracyBandAggregate[]): ProgressBarBand[] => {
    const total: number = bandAggregates.reduce((sum, x) => sum + x.providerCount, 0);

    return bandAggregates.map<ProgressBarBand>((a) => {
      const percent = total > 0 ? (a.providerCount / total) * 100 : 0;
      return {
        color: a.color,
        colorType: "hex",
        justification: a.justification,
        outOfScale: percent > 0 && percent < 0.1,
        percent: this.getBandPercent(percent),
        tooltip: `${a.name ? a.name + ": " : ""}${Intl.NumberFormat("en-US").format(a.providerCount)} providers`,
      };
    });
  }

  /**
   * Enforces smallest increment is rendered if percent value is smaller than 0.1
   * @param percent percent value in question
   */
  private getBandPercent = (percent: number) => {
    return percent
      ? (percent > 0 && percent < 0.1) ? 0.3 : percent // 0.3 is the smallest visible segment
      : 0;
  }

  private getPrefixedKey = (record: FlatAggregateRecord): string => `${record.groupName}-${record.inspectionUid}`;
}

const WrappedAccuracyAggregateInspectionDetailsByGroup = withCountyProjectContext(AccuracyAggregateInspectionDetailsByGroup);

export default WrappedAccuracyAggregateInspectionDetailsByGroup;
